import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, getFirestore, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBriefcase,
  faBuilding,
  faCalendarDay,
  faClipboardList,
  faCoins,
  faEnvelope,
  faFileInvoiceDollar,
  faIdCard,
  faMoneyCheckDollar,
  faPhone,
  faShieldHalved,
  faUser,
  faUserClock
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { app } from '../../firebase';
import '../dash.css';
import {
  compareMonthKeys,
  formatDateTime,
  formatKes,
  formatShortDate,
  getEmployeeSalaryForPeriod,
  getMonthKey,
  getMonthLabel,
  isSuccessfulSalaryPayment,
  resolveSalaryPaymentPeriodKey
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';

const PAYMENT_METHOD_OPTIONS = [
  'Bank Transfer',
  'MPESA',
  'Cash',
  'Cheque',
  'Other'
];

const trimValue = (value) => String(value || '').trim();

const buildPayrollForm = (employee = {}) => ({
  salaryPaymentMethod: employee.salaryPaymentMethod || PAYMENT_METHOD_OPTIONS[0],
  salaryPaymentBankId: employee.salaryPaymentBankId || '',
  salaryPaymentBankName: employee.salaryPaymentBankName || '',
  salaryPaymentBankCode: employee.salaryPaymentBankCode || '',
  salaryPaymentReference: employee.salaryPaymentReference || '',
  nssfNumber: employee.nssfNumber || '',
  nhifShaNumber: employee.nhifShaNumber || '',
  kraPin: employee.kraPin || '',
  payrollNotes: employee.payrollNotes || ''
});

const normalizeEmployeeStatus = (status) => String(status || 'Active').trim();
const getEmployeeStatusClass = (status) => normalizeEmployeeStatus(status).toLowerCase();

const hasLockedPayrollPaymentDetails = (employee = {}) => {
  const paymentMethod = trimValue(employee.salaryPaymentMethod);
  const bankId = trimValue(employee.salaryPaymentBankId);
  const bankName = trimValue(employee.salaryPaymentBankName);
  const paymentReference = trimValue(employee.salaryPaymentReference);

  if (employee.salaryPaymentDetailsLocked) {
    return true;
  }

  if (!paymentMethod || !paymentReference) {
    return false;
  }

  if (paymentMethod === 'Bank Transfer') {
    return Boolean(bankId || bankName);
  }

  return true;
};

function ViewEmployeeComp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = getFirestore(app);
  const auth = getAuth();
  const hasLoggedViewRef = useRef(false);

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [savingPayroll, setSavingPayroll] = useState(false);
  const [salaryAdjusting, setSalaryAdjusting] = useState(false);
  const [salaryAdjustModalShow, setSalaryAdjustModalShow] = useState(false);
  const [salaryLogsModalShow, setSalaryLogsModalShow] = useState(false);
  const [salaryAdjustForm, setSalaryAdjustForm] = useState({
    salary: '',
    effectiveFrom: getMonthKey(),
    reason: ''
  });
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankLoadError, setBankLoadError] = useState('');
  const [payrollForm, setPayrollForm] = useState(buildPayrollForm());

  const loadEmployeePage = useCallback(async () => {
    if (!id) {
      setEmployee(null);
      return;
    }

    const employeeSnap = await getDoc(doc(firestore, 'Employees', id));

    if (!employeeSnap.exists()) {
      setEmployee(null);
      return;
    }

    setEmployee({ id: employeeSnap.id, ...employeeSnap.data() });
  }, [firestore, id]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadEmployeePage();
      } catch (error) {
        console.error('Failed to load employee profile:', error);
        swal('Error', 'Employee details could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadEmployeePage]);

  useEffect(() => {
    if (!employee) return;
    setPayrollForm(buildPayrollForm(employee));
  }, [employee]);

  const mapLotusBankOptions = useCallback((payload) => {
    const rawBanks = payload?.data?.banks || payload?.data?.bank_list || payload?.banks || [];
    if (!Array.isArray(rawBanks)) {
      return [];
    }

    return rawBanks
      .map((bank) => {
        const bankId = String(bank?.id || bank?.bank_id || bank?.bankId || bank?.code || bank?.bank_code || '').trim();
        const bankName = String(
          bank?.name || bank?.bank_name || bank?.bankName || bank?.display_name || bank?.label || bankId || 'Bank'
        ).trim();

        return {
          id: bankId || bankName,
          name: bankName,
          code: String(bank?.code || bank?.bank_code || bank?.bankCode || '').trim(),
          raw: bank
        };
      })
      .filter((bank) => bank.id || bank.name);
  }, []);

  const fetchLotusBanks = useCallback(async () => {
    if (banksLoaded || loadingBanks) {
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setBankLoadError('Sign in again to load bank options.');
      return;
    }

    setLoadingBanks(true);
    setBankLoadError('');

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${LOTUS_API_BASE_URL}/banks`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to load banks.');
      }

      setBankOptions(mapLotusBankOptions(payload));
      setBanksLoaded(true);
    } catch (error) {
      console.error('Failed to load Lotus banks:', error);
      setBankLoadError('Banks could not be loaded right now.');
      swal('Bank list unavailable', 'We could not load the Lotus bank list right now.', 'warning');
    } finally {
      setLoadingBanks(false);
    }
  }, [auth, banksLoaded, loadingBanks, mapLotusBankOptions]);

  useEffect(() => {
    if (payrollForm.salaryPaymentMethod === 'Bank Transfer' && !banksLoaded) {
      fetchLotusBanks();
    }
  }, [banksLoaded, fetchLotusBanks, payrollForm.salaryPaymentMethod]);

  useEffect(() => {
    if (!employee || hasLoggedViewRef.current) return;

    logAdminActivity({
      action: 'Viewed Employee Profile',
      details: `Opened employee profile for ${employee.employeeName || 'employee'}`,
      meta: {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        employeeSection: 'profile'
      }
    });

    hasLoggedViewRef.current = true;
  }, [employee]);

  const employeeStatus = normalizeEmployeeStatus(employee?.employeeStatus);
  const employeeStatusClass = getEmployeeStatusClass(employee?.employeeStatus);
  const salaryAmount = getEmployeeSalaryForPeriod(employee || {}, getMonthKey());
  const salaryChangeLogs = useMemo(() => (
    (Array.isArray(employee?.salaryHistory) ? employee.salaryHistory : [])
      .filter((entry) => entry && !entry.isBaseline)
      .sort((first, second) => (
        (second.recordedAt?.toMillis?.() || 0) - (first.recordedAt?.toMillis?.() || 0)
      ))
  ), [employee?.salaryHistory]);
  const payrollPaymentDetailsLocked = useMemo(
    () => hasLockedPayrollPaymentDetails(employee || {}),
    [employee]
  );
  const payrollCompleteness = useMemo(() => {
    const fields = [
      payrollForm.salaryPaymentMethod,
      payrollForm.salaryPaymentMethod === 'Bank Transfer' ? payrollForm.salaryPaymentBankId : '',
      payrollForm.salaryPaymentReference,
      payrollForm.nssfNumber,
      payrollForm.nhifShaNumber,
      payrollForm.kraPin
    ];
    return fields.filter((value) => String(value || '').trim()).length;
  }, [payrollForm]);

  const managementActions = useMemo(() => {
    const actions = [
      {
        title: 'View leave requests',
        description: 'Track leave applications, dates, approval status, and notes.',
        path: `/view-employee/${id}/leave-requests`,
        icon: faCalendarDay
      },
      {
        title: 'View salary payments',
        description: 'Open the dedicated payroll page to record and review salary payments.',
        path: `/view-employee/${id}/salary-payments`,
        icon: faMoneyCheckDollar
      },
      {
        title: 'View activity logs',
        description: 'Review admin actions captured against this employee profile.',
        path: `/view-employee/${id}/activity-logs`,
        icon: faClipboardList
      },
      {
        title: 'View teaching sessions',
        description: 'See classes and teaching sessions linked to this employee as instructor.',
        path: `/view-employee/${id}/teaching-sessions`,
        icon: faUserClock
      },
      {
        title: 'View check-in time',
        description: 'Manage attendance, check-in history, and manual clock records.',
        path: `/view-employee/${id}/check-ins`,
        icon: faUser
      },
      {
        title: 'View disciplinary records',
        description: 'Keep warnings, disciplinary actions, and follow-up notes in one place.',
        path: `/view-employee/${id}/disciplinary-records`,
        icon: faShieldHalved
      }
    ];

    return actions;
  }, [id]);

  const employeeInfoCards = [
    {
      icon: faBuilding,
      label: 'Campus',
      value: employee?.campus || 'Not set'
    },
    {
      icon: faBriefcase,
      label: 'Engagement',
      value: employee?.engagementType || 'Not set'
    },
    {
      icon: faCalendarDay,
      label: 'Start date',
      value: formatShortDate(employee?.employeeStartDate, 'Not set')
    },
    {
      icon: faIdCard,
      label: 'Passport or ID',
      value: employee?.employeeID || 'Not set'
    },
    {
      icon: faUser,
      label: 'Date of birth',
      value: formatShortDate(employee?.addEmployeeAge, 'Not set')
    },
    {
      icon: faCoins,
      label: 'Base salary',
      value: formatKes(salaryAmount)
    }
  ];

  const handleToggleStatus = async () => {
    if (!employee?.id) return;
    const currentStatus = employee.employeeStatus || 'Active';
    const nextStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

    setUpdatingStatus(true);
    try {
      await updateDoc(doc(firestore, 'Employees', employee.id), {
        employeeStatus: nextStatus,
        updatedAt: Timestamp.now()
      });
      setEmployee((currentEmployee) => (
        currentEmployee ? { ...currentEmployee, employeeStatus: nextStatus } : currentEmployee
      ));

      await logAdminActivity({
        action: 'Updated Employee Status',
        details: `Set ${employee.employeeName || 'employee'} to ${nextStatus}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          employeeStatus: nextStatus
        }
      });

      swal('Updated', `Employee status set to ${nextStatus}.`, 'success');
    } catch (error) {
      console.error('Failed to update employee status:', error);
      swal('Error', 'Failed to update employee status.', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openSalaryAdjustModal = () => {
    setSalaryAdjustForm({
      salary: String(getEmployeeSalaryForPeriod(employee || {}, getMonthKey())),
      effectiveFrom: getMonthKey(),
      reason: ''
    });
    setSalaryAdjustModalShow(true);
  };

  const handleSalaryAdjustFieldChange = (event) => {
    const { name, value } = event.target;
    setSalaryAdjustForm((currentForm) => ({ ...currentForm, [name]: value }));
  };

  const handleSaveSalaryAdjustment = async () => {
    if (!employee?.id) return;

    const nextSalary = Number(salaryAdjustForm.salary);
    const effectiveFrom = String(salaryAdjustForm.effectiveFrom || '').trim();
    const reason = salaryAdjustForm.reason.trim();
    if (!Number.isFinite(nextSalary) || nextSalary <= 0) {
      swal('Invalid salary', 'Enter a salary amount greater than zero.', 'warning');
      return;
    }
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(effectiveFrom)) {
      swal('Choose an effective month', 'Select the month when the new salary should start.', 'warning');
      return;
    }
    if (!reason) {
      swal('Add a reason', 'Enter a short reason for this salary change.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please sign in again before changing salary.', 'warning');
      return;
    }

    setSalaryAdjusting(true);
    try {
      let salaryHistory = Array.isArray(employee.salaryHistory)
        ? employee.salaryHistory.filter((entry) => entry && /^\d{4}-(0[1-9]|1[0-2])$/.test(entry.effectiveFrom || ''))
        : [];

      if (salaryHistory.length === 0) {
        const paymentSnapshot = await getDocs(query(
          collection(firestore, 'SalaryPayments'),
          where('employeeId', '==', employee.id)
        ));
        const historicalPayrollMonths = paymentSnapshot.docs
          .map((paymentDoc) => ({ id: paymentDoc.id, ...paymentDoc.data() }))
          .filter(isSuccessfulSalaryPayment)
          .map(resolveSalaryPaymentPeriodKey)
          .filter((monthKey) => /^\d{4}-(0[1-9]|1[0-2])$/.test(monthKey))
          .sort(compareMonthKeys);
        const firstTrackedMonth = historicalPayrollMonths[0] || getMonthKey();
        const baselineMonth = compareMonthKeys(firstTrackedMonth, effectiveFrom) <= 0
          ? firstTrackedMonth
          : effectiveFrom;
        salaryHistory = [{
          salary: Number(employee.salary || 0),
          effectiveFrom: baselineMonth,
          recordedAt: Timestamp.now(),
          recordedBy: currentUser.uid,
          recordedByName: currentUser.displayName || currentUser.email || 'Admin',
          reason: 'Salary before first tracked adjustment',
          isBaseline: true
        }];
      }

      const previousSalary = getEmployeeSalaryForPeriod({ ...employee, salaryHistory }, effectiveFrom);
      if (previousSalary === nextSalary) {
        swal('No salary change', 'The salary amount is already set to this value for that month.', 'info');
        return;
      }

      const recordedAt = Timestamp.now();
      const adjustment = {
        salary: nextSalary,
        previousSalary,
        effectiveFrom,
        recordedAt,
        recordedBy: currentUser.uid,
        recordedByName: currentUser.displayName || currentUser.email || 'Admin',
        reason,
        isBaseline: false
      };
      salaryHistory = [...salaryHistory, adjustment].sort((first, second) => {
        const monthOrder = compareMonthKeys(first.effectiveFrom, second.effectiveFrom);
        if (monthOrder !== 0) return monthOrder;
        return (first.recordedAt?.toMillis?.() || 0) - (second.recordedAt?.toMillis?.() || 0);
      });
      const updatedEmployee = { ...employee, salaryHistory };
      const currentSalary = getEmployeeSalaryForPeriod(updatedEmployee, getMonthKey());
      const payload = {
        salary: currentSalary,
        salaryHistory,
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(firestore, 'Employees', employee.id), payload);
      setEmployee((currentEmployee) => currentEmployee ? { ...currentEmployee, ...payload } : currentEmployee);
      await logAdminActivity({
        action: 'Adjusted Employee Salary',
        details: `Changed ${employee.employeeName || 'employee'} salary to ${formatKes(nextSalary)} effective ${getMonthLabel(effectiveFrom)}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          previousSalary,
          newSalary: nextSalary,
          effectiveFrom,
          reason
        }
      });

      setSalaryAdjustModalShow(false);
      swal('Salary updated', `New salary of ${formatKes(nextSalary)} will apply from ${getMonthLabel(effectiveFrom)}.`, 'success');
    } catch (error) {
      console.error('Failed to save salary adjustment:', error);
      swal('Error', error.message || 'The salary adjustment could not be saved.', 'error');
    } finally {
      setSalaryAdjusting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!employee?.employeeEmail) {
      swal('Missing Email', 'This employee has no email on record.', 'error');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, employee.employeeEmail);
      await logAdminActivity({
        action: 'Sent Employee Password Reset',
        details: `Sent password reset email to ${employee.employeeName || 'employee'}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          employeeEmail: employee.employeeEmail || ''
        }
      });
      swal('Sent', 'Password reset email sent successfully.', 'success');
    } catch (error) {
      console.error('Failed to send reset email:', error);
      swal('Error', 'Could not send password reset email.', 'error');
    }
  };

  const handlePayrollFieldChange = (event) => {
    const { name, value } = event.target;

    if (payrollPaymentDetailsLocked && name.startsWith('salaryPayment')) {
      return;
    }

    setPayrollForm((currentForm) => ({
      ...currentForm,
      [name]: value,
      ...(name === 'salaryPaymentMethod' && value !== 'Bank Transfer'
        ? {
            salaryPaymentBankId: '',
            salaryPaymentBankName: '',
            salaryPaymentBankCode: ''
          }
        : {})
    }));

    if (name === 'salaryPaymentMethod' && value === 'Bank Transfer') {
      fetchLotusBanks();
    }
  };

  const handleBankSelect = (event) => {
    if (payrollPaymentDetailsLocked) {
      return;
    }

    const selectedBankId = event.target.value;
    const selectedBank = bankOptions.find((bank) => bank.id === selectedBankId) || null;

    setPayrollForm((currentForm) => ({
      ...currentForm,
      salaryPaymentBankId: selectedBank?.id || '',
      salaryPaymentBankName: selectedBank?.name || '',
      salaryPaymentBankCode: selectedBank?.code || ''
    }));
  };

  const handleSavePayrollDetails = async () => {
    if (!employee?.id) return;

    if (!String(payrollForm.salaryPaymentMethod || '').trim()) {
      swal('Missing information', 'Please choose the salary payment method.', 'warning');
      return;
    }

    if (payrollForm.salaryPaymentMethod === 'Bank Transfer' && !String(payrollForm.salaryPaymentBankId || '').trim()) {
      swal('Missing information', 'Please choose the bank for bank transfer payments.', 'warning');
      return;
    }

    if (!payrollPaymentDetailsLocked && !String(payrollForm.salaryPaymentReference || '').trim()) {
      swal(
        'Missing information',
        payrollForm.salaryPaymentMethod === 'Bank Transfer'
          ? 'Please enter the employee bank account number.'
          : 'Please enter the payment reference.',
        'warning'
      );
      return;
    }

    setSavingPayroll(true);
    try {
      const nextSalaryPaymentMethod = payrollPaymentDetailsLocked
        ? trimValue(employee?.salaryPaymentMethod)
        : trimValue(payrollForm.salaryPaymentMethod);
      const nextSalaryPaymentBankId = payrollPaymentDetailsLocked
        ? trimValue(employee?.salaryPaymentBankId)
        : trimValue(payrollForm.salaryPaymentBankId);
      const nextSalaryPaymentBankName = payrollPaymentDetailsLocked
        ? trimValue(employee?.salaryPaymentBankName)
        : trimValue(payrollForm.salaryPaymentBankName);
      const nextSalaryPaymentBankCode = payrollPaymentDetailsLocked
        ? trimValue(employee?.salaryPaymentBankCode)
        : trimValue(payrollForm.salaryPaymentBankCode);
      const nextSalaryPaymentReference = payrollPaymentDetailsLocked
        ? trimValue(employee?.salaryPaymentReference)
        : trimValue(payrollForm.salaryPaymentReference);
      const nextPayrollPaymentDetailsLocked = Boolean(
        payrollPaymentDetailsLocked ||
        (
          nextSalaryPaymentMethod === 'Bank Transfer'
            ? (nextSalaryPaymentBankId || nextSalaryPaymentBankName) && nextSalaryPaymentReference
            : nextSalaryPaymentMethod && nextSalaryPaymentReference
        )
      );

      const payload = {
        salaryPaymentMethod: nextSalaryPaymentMethod,
        salaryPaymentBankId: nextSalaryPaymentBankId,
        salaryPaymentBankName: nextSalaryPaymentBankName,
        salaryPaymentBankCode: nextSalaryPaymentBankCode,
        salaryPaymentReference: nextSalaryPaymentReference,
        salaryPaymentDetailsLocked: nextPayrollPaymentDetailsLocked,
        nssfNumber: payrollForm.nssfNumber.trim(),
        nhifShaNumber: payrollForm.nhifShaNumber.trim(),
        kraPin: payrollForm.kraPin.trim(),
        payrollNotes: payrollForm.payrollNotes.trim(),
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(firestore, 'Employees', employee.id), payload);
      setEmployee((currentEmployee) => (
        currentEmployee ? { ...currentEmployee, ...payload } : currentEmployee
      ));

      await logAdminActivity({
        action: 'Updated Employee Payroll Details',
        details: `Saved payroll and statutory details for ${employee.employeeName || 'employee'}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          salaryPaymentMethod: payload.salaryPaymentMethod
        }
      });

      swal('Saved', 'Payroll details updated successfully.', 'success');
    } catch (error) {
      console.error('Failed to update payroll details:', error);
      swal('Error', 'Could not save payroll details.', 'error');
    } finally {
      setSavingPayroll(false);
    }
  };

  if (loading) {
    return (
      <div className="employeeDirectoryPage employeeProfileViewPage">
        <div className="employeeDirectoryPanel employeeProfilePanel">
          <div className="opsLoadingCard">
            <Spinner animation="border" role="status" />
            <span>Loading employee...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="employeeDirectoryPage employeeProfileViewPage">
        <div className="employeeDirectoryPanel employeeProfilePanel">
          <div className="opsEmptyState">
            <h6>Employee not found</h6>
            <p>The employee profile you are looking for could not be found.</p>
            <button type="button" className="employeeProfileBackBtn" onClick={() => navigate('/employees')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to employees
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employeeDirectoryPage employeeProfileViewPage employeeManagementHubPage">
      <section className="employeeProfileHero">
        <div className="employeeProfileHeroTop">
          <button type="button" className="employeeProfileBackBtn" onClick={() => navigate('/employees')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to employees</span>
          </button>
        </div>

        <div className="employeeProfileHeroBody">
          <div className="employeeProfileHeroCopy">
            <p className="employeeDirectoryEyebrow">Employee management</p>
            <div className="employeeProfileTitleRow">
              <h3>{employee.employeeName || 'Employee'}</h3>
              <span className={`employeeStatusBadge ${employeeStatusClass}`}>{employeeStatus}</span>
            </div>
            <p className="employeeProfileLead">
              Manage payroll details, statutory numbers, profile health, and operational records from one workspace.
            </p>
            <div className="employeeDirectoryHeroMeta">
              <span>{employee.jobPosition || 'Role not set'}</span>
              <span>{employee.campus || 'Campus not set'}</span>
              <span>{employee.employeeEmail || 'Email not set'}</span>
            </div>
          </div>
          <div className="employeeProfileHeroActions">
            <button type="button" className="employeeDirectoryPrimaryBtn" onClick={handleResetPassword}>
              Reset Password
            </button>
            <button type="button" className="employeeProfileActionBtn" onClick={handleToggleStatus} disabled={updatingStatus}>
              {employeeStatus === 'Active' ? 'Mark inactive' : 'Activate employee'}
            </button>
          </div>
        </div>
      </section>

      <section className="employeeDirectoryStats employeeProfileStats employeeManagementHubStats">
        <article className="employeeDirectoryStatCard">
          <span>Base salary</span>
          <strong>{formatKes(salaryAmount)}</strong>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button size="sm" variant="dark" onClick={openSalaryAdjustModal}>
              Adjust salary
            </Button>
            <Button size="sm" variant="outline-dark" onClick={() => setSalaryLogsModalShow(true)}>
              View change logs ({salaryChangeLogs.length})
            </Button>
          </div>
        </article>
        <article className="employeeDirectoryStatCard">
          <span>Payment method</span>
          <strong>{employee.salaryPaymentMethod || 'Not set'}</strong>
        </article>
        <article className="employeeDirectoryStatCard">
          <span>NSSF number</span>
          <strong>{employee.nssfNumber || 'Not set'}</strong>
        </article>
        <article className="employeeDirectoryStatCard">
          <span>NHIF / SHA number</span>
          <strong>{employee.nhifShaNumber || 'Not set'}</strong>
        </article>
      </section>

      <section className="employeeManagementHubGrid">
        <article className="employeeDirectoryPanel employeeProfilePanel employeeManagementHubPanel">
          <div className="employeeProfilePanelHeader">
            <div>
              <h4>Management workspace</h4>
              <p>Open the employee areas you need without crowding the main profile page.</p>
            </div>
          </div>

          <div className="employeeManagementActionGrid">
            {managementActions.map((action) => (
              <button
                key={action.title}
                type="button"
                className="employeeManagementActionCard"
                onClick={() => navigate(action.path)}
              >
                <span className="employeeManagementActionIcon">
                  <FontAwesomeIcon icon={action.icon} />
                </span>
                <strong>{action.title}</strong>
                <p>{action.description}</p>
                <span className="employeeManagementActionLink">Open</span>
              </button>
            ))}
          </div>

          <div className="employeeProfilePanelHeader employeeManagementHubSubheader">
            <div>
              <h4>Profile snapshot</h4>
              <p>Key employee details that matter most when handling HR and payroll operations.</p>
            </div>
          </div>

          <div className="employeeManagementInfoGrid">
            {employeeInfoCards.map((item) => (
              <div key={item.label} className="employeeManagementInfoCard">
                <span><FontAwesomeIcon icon={item.icon} /> {item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </article>

        <aside className="employeeProfileSidebar">
          <div className="employeeDirectoryPanel employeeProfilePanel employeeManagementHubPanel">
            <div className="employeeProfilePanelHeader employeeProfilePanelHeader--compact">
              <div>
                <h4>Payroll and compliance</h4>
                <p>Save how this employee is paid and keep their statutory references ready for payroll work.</p>
              </div>
              <div className="employeeProfilePanelIcon">
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
              </div>
            </div>

            <div className="employeeManagementMiniMetrics">
              <div className="employeeManagementMiniMetric">
                <span>Payroll profile filled</span>
                <strong>{payrollCompleteness}/5 fields</strong>
              </div>
              <div className="employeeManagementMiniMetric">
                <span>KRA PIN</span>
                <strong>{employee.kraPin || 'Not set'}</strong>
              </div>
            </div>

            <div className="employeeManagementFormGrid">
              <Form.Group className="mb-3">
                <Form.Label>Salary payment method</Form.Label>
                <Form.Select
                  name="salaryPaymentMethod"
                  value={payrollForm.salaryPaymentMethod}
                  onChange={handlePayrollFieldChange}
                  disabled={payrollPaymentDetailsLocked}
                >
                  {PAYMENT_METHOD_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {payrollForm.salaryPaymentMethod === 'Bank Transfer' && (
                <Form.Group className="mb-3">
                  <Form.Label>Bank name</Form.Label>
                  <Form.Select
                    value={payrollForm.salaryPaymentBankId}
                    onChange={handleBankSelect}
                    disabled={loadingBanks || payrollPaymentDetailsLocked}
                  >
                    <option value="">{loadingBanks ? 'Loading banks...' : 'Select bank from Lotus'}</option>
                    {bankOptions.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.name}{bank.code ? ` • ${bank.code}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  {bankLoadError && <small className="text-danger d-block mt-2">{bankLoadError}</small>}
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>
                  {payrollForm.salaryPaymentMethod === 'Bank Transfer' ? 'Bank account number' : 'Payment reference'}
                </Form.Label>
                <Form.Control
                  name="salaryPaymentReference"
                  value={payrollForm.salaryPaymentReference}
                  onChange={handlePayrollFieldChange}
                  readOnly={payrollPaymentDetailsLocked}
                  placeholder={
                    payrollForm.salaryPaymentMethod === 'Bank Transfer'
                      ? 'Employee bank account number'
                      : 'Bank account, till, paybill, or account hint'
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>NSSF number</Form.Label>
                <Form.Control
                  name="nssfNumber"
                  value={payrollForm.nssfNumber}
                  onChange={handlePayrollFieldChange}
                  placeholder="NSSF number"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>NHIF / SHA number</Form.Label>
                <Form.Control
                  name="nhifShaNumber"
                  value={payrollForm.nhifShaNumber}
                  onChange={handlePayrollFieldChange}
                  placeholder="NHIF or SHA number"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>KRA PIN</Form.Label>
                <Form.Control
                  name="kraPin"
                  value={payrollForm.kraPin}
                  onChange={handlePayrollFieldChange}
                  placeholder="Useful for payroll and tax records"
                />
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>Payroll notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="payrollNotes"
                  value={payrollForm.payrollNotes}
                  onChange={handlePayrollFieldChange}
                  placeholder="Optional notes about payout handling or payroll exceptions"
                />
              </Form.Group>
            </div>

            <p className="employeeManagementHelperText">
              KRA PIN is included as an extra field because it usually helps the payroll workflow alongside NSSF and NHIF/SHA.
              {payrollForm.salaryPaymentMethod === 'Bank Transfer' ? ' Bank selection is loaded from Lotus.' : ''}
              {payrollPaymentDetailsLocked ? ' These payment details are locked after saving and can only be changed from Firebase.' : ''}
            </p>

            <Button variant="dark" className="w-100" onClick={handleSavePayrollDetails} disabled={savingPayroll}>
              {savingPayroll ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> Saving...
                </>
              ) : (
                'Save payroll details'
              )}
            </Button>
          </div>

          <div className="employeeDirectoryPanel employeeProfilePanel employeeManagementHubPanel">
            <div className="employeeProfilePanelHeader">
              <div>
                <h4>Contact and identity</h4>
                <p>Quick references and copy actions for the details you will reuse most often.</p>
              </div>
            </div>

            <div className="employeeProfileMiniBlock employeeProfileMiniBlock--flush">
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faEnvelope} /> Work email</span>
                <strong>{employee.employeeEmail || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faPhone} /> Phone</span>
                <strong>{employee.employeePhone || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span>Personal email</span>
                <strong>{employee.currentEmail || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faShieldHalved} /> Tax type</span>
                <strong>{employee.taxType || 'Not set'}</strong>
              </div>
            </div>

          </div>
        </aside>
      </section>

      <Modal show={salaryAdjustModalShow} onHide={() => !salaryAdjusting && setSalaryAdjustModalShow(false)} centered>
        <Modal.Header closeButton={!salaryAdjusting}>
          <Modal.Title>Adjust salary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            The new amount applies from the selected month. Earlier months keep the salary rate that was effective then.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>New monthly salary</Form.Label>
            <Form.Control
              type="number"
              name="salary"
              min="0.01"
              step="0.01"
              value={salaryAdjustForm.salary}
              onChange={handleSalaryAdjustFieldChange}
              disabled={salaryAdjusting}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Effective from</Form.Label>
            <Form.Control
              type="month"
              name="effectiveFrom"
              value={salaryAdjustForm.effectiveFrom}
              onChange={handleSalaryAdjustFieldChange}
              disabled={salaryAdjusting}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Reason for change</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="reason"
              value={salaryAdjustForm.reason}
              onChange={handleSalaryAdjustFieldChange}
              placeholder="For example: annual salary review"
              disabled={salaryAdjusting}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSalaryAdjustModalShow(false)} disabled={salaryAdjusting}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleSaveSalaryAdjustment} disabled={salaryAdjusting}>
            {salaryAdjusting ? <><Spinner as="span" animation="border" size="sm" /> Saving...</> : 'Save salary change'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={salaryLogsModalShow} onHide={() => setSalaryLogsModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Salary change logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {salaryChangeLogs.length === 0 ? (
            <div className="text-muted">No salary changes have been recorded yet.</div>
          ) : (
            <div className="d-grid gap-3">
              {salaryChangeLogs.map((change, index) => (
                <div key={`${change.effectiveFrom}-${change.recordedAt?.toMillis?.() || index}`} className="border rounded p-3">
                  <div className="d-flex justify-content-between gap-3">
                    <strong>{formatKes(change.previousSalary)} → {formatKes(change.salary ?? change.amount)}</strong>
                    <span className="text-muted">{getMonthLabel(change.effectiveFrom)}</span>
                  </div>
                  <div className="small text-muted mt-1">
                    Recorded by {change.recordedByName || 'Admin'} · {formatDateTime(change.recordedAt)}
                  </div>
                  {change.reason && <div className="mt-2">{change.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ViewEmployeeComp;
